import { useState } from 'react';

import { ACCENT_COLOR } from '@/constants';
import { getPlatformURL, blurActiveElement } from '@/utils';

import PlatformIcon from '../PlatformIcon';
import CompletionStatus from './CompletionStatus';

const ConversionRenderer = ({
  source,
  target,
  progress,
  isConverting,
  playlistCount,
  skippedPlaylistCount,
}) => {
  const url = getPlatformURL(target);

  const [copied, setCopied] = useState(false);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000); // Reset after 2 seconds
    blurActiveElement();
  };

  const commonIconProps = {
    ariaHidden: true,
    className: 'me-3 flex-shrink-0',
  };

  return (
    <div className="conversion-renderer card mb-2 shadow-sm">
      <div className="card-body py-3">
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center overflow-hidden">
            <div className="text-start overflow-hidden w-100">
              <div className="d-flex align-items-center">
                <PlatformIcon {...commonIconProps} platform={source} />

                <i
                  aria-hidden="true"
                  style={{ fontSize: '1.2rem', color: ACCENT_COLOR }}
                  className={`fa fa-arrow-right me-3 flex-shrink-0 ${isConverting ? 'arrow-animating' : ''}`}
                ></i>

                <PlatformIcon {...commonIconProps} platform={target} />

                <div className="d-flex flex-column flex-sm-row align-items-sm-center">
                  <h6
                    className="mb-0 me-sm-2 text-truncate"
                    style={{ maxWidth: '100%', minWidth: 0 }}
                  >
                    {isConverting
                      ? 'Please wait'
                      : playlistCount > 0
                        ? 'Transferred'
                        : 'Complete'}
                  </h6>

                  <div className="mt-1">
                    {isConverting ? (
                      <>
                        <small className="text-accent d-flex align-items-center flex-shrink-0 mb-1">
                          <i
                            aria-hidden="true"
                            style={{ fontSize: '0.75rem' }}
                            className="fas fa-spinner fa-spin me-1"
                          ></i>
                          <span>Converting...</span>
                        </small>

                        {progress !== null && (
                          <div className="progress" style={{ height: '4px' }}>
                            <div
                              role="progressbar"
                              style={{
                                width: `${progress}%`,
                                transition: 'width 0.3s ease',
                                backgroundColor: ACCENT_COLOR,
                              }}
                              className="progress-bar progress-bar-striped progress-bar-animated"
                            ></div>
                          </div>
                        )}
                      </>
                    ) : (
                      <CompletionStatus
                        playlistCount={playlistCount}
                        skippedPlaylistCount={skippedPlaylistCount}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="d-flex gap-2 ms-2 flex-shrink-0">
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              aria-disabled={isConverting}
              aria-label={`Open ${target}`}
              className={`btn btn-sm btn-outline-custom btn-outline-${target}`}
            >
              <i className="fas fa-external-link-alt" aria-hidden="true"></i>
              <span className="d-none d-sm-inline ms-1">Open</span>
            </a>

            <button
              disabled={isConverting}
              onClick={copyToClipboard}
              title="Copy music platform link"
              aria-label="Copy music platform link"
              className={`btn btn-sm btn-outline-custom btn-outline-${target}`}
            >
              <i
                aria-hidden="true"
                className={`fas ${copied ? 'fa-check' : 'fa-copy'}`}
              ></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConversionRenderer;
